import { Caret } from '@on3/ui-lib/components/Svg/Caret';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { useClickOutside } from '@on3/ui-lib/hooks/useClickOutside';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import styles from './Filter.module.scss';

export interface FilterOption {
  label: string;
  value: string;
}

interface FilterProps {
  name: string;
  options: FilterOption[];
  value: string | null;
  navigate: (name: string, value: string) => string;
  placeholder?: string;
  [key: string]: any;
}

export const Filter = ({
  name,
  options = [],
  value,
  navigate,
  placeholder = '',
  className,
}: FilterProps) => {
  const router = useRouter();
  const { currentSite } = useSite();
  const primaryColor =
    currentSite?.linkColor || currentSite?.primaryColor || '#fe3b1f';

  const filterStyles = { color: primaryColor };
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const filterRef = useRef<HTMLDivElement>(null);

  const close = () => setIsOpen(false);

  useClickOutside(filterRef, close);

  useEffect(() => {
    if (filterRef.current) {
      const viewportWidth = window?.innerWidth;
      const filterPositionX = filterRef.current.getBoundingClientRect().x;
      const placeInViewport = filterPositionX / viewportWidth;

      const alignRight = placeInViewport > 0.5;

      setDropdownStyle({
        ...(alignRight
          ? { left: 'auto', right: 0 }
          : { left: 0, right: 'auto' }),
      });
    }
  }, []);

  useEffect(() => {
    router.events.on('routeChangeComplete', close);

    return () => {
      router.events.off('routeChangeComplete', close);
    };
  }, [router.events]);

  options = placeholder
    ? [{ label: placeholder, value: '' }, ...options]
    : options;

  const selectedOption = options.find((o) => o.value === value) || options[0];

  if (options.length < 1) {
    return null;
  }

  return (
    <div
      aria-controls={`filter-options-${name}`}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-owns={`filter-options-${name}`}
      className={clsx(styles.base, className)}
      ref={filterRef}
      role="combobox"
    >
      <button
        aria-controls={`filter-options-${name}`}
        aria-label={`Select option: ${selectedOption.label}`}
        className={clsx(styles.filter, {
          [styles.active]: isOpen,
        })}
        data-ui="filter"
        onClick={() => setIsOpen(!isOpen)}
        style={filterStyles}
        type="button"
      >
        {selectedOption.label}
        <Caret />
      </button>
      <ul
        aria-label="Filter options"
        className={clsx(styles.dropdown, {
          [styles.open]: isOpen,
        })}
        id={`filter-options-${name}`}
        role="listbox"
        style={dropdownStyle}
      >
        {options.map((option: FilterOption) => (
          <li
            aria-selected={option.value === value}
            key={option.value}
            role="option"
          >
            <Link href={navigate(name, option.value)}>{option.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
